import React, { useEffect, useRef } from "react";
import { motion } from "framer-motion";
import "./ASMRGhibliScene.css";

export default function ASMRGhibliScene() {
  const canvasRef = useRef(null);
  const rafRef = useRef(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");

    const resize = () => {
      const dpr = Math.min(2, window.devicePixelRatio || 1);
      canvas.width = Math.floor(window.innerWidth * dpr);
      canvas.height = Math.floor(window.innerHeight * dpr);
      canvas.style.width = "100%";
      canvas.style.height = "100%";
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };

    resize();
    window.addEventListener("resize", resize);

    // ---------- “Ghibli-ish” scene data ----------
    const particles = Array.from({ length: 110 }, () => ({
      x: Math.random() * canvas.width,
      y: Math.random() * canvas.height,
      r: 0.8 + Math.random() * 2.1,
      a: 0.12 + Math.random() * 0.22, // soft ink haze
      vx: (Math.random() - 0.5) * 0.18,
      vy: (Math.random() - 0.5) * 0.18,
    }));

    const lanterns = Array.from({ length: 5 }, () => ({
      x: (0.12 + Math.random() * 0.76) * canvas.width,
      y: (0.18 + Math.random() * 0.55) * canvas.height,
      s: 0.9 + Math.random() * 0.65,
      drift: 0.9 + Math.random() * 1.2,
      wob: 0.9 + Math.random() * 1.6,
      glow: 0.55 + Math.random() * 0.35,
    }));

    const rain = Array.from({ length: 170 }, () => ({
      x: Math.random() * canvas.width,
      y: Math.random() * canvas.height,
      len: 10 + Math.random() * 18,
      sp: 2.2 + Math.random() * 2.2,
      a: 0.08 + Math.random() * 0.16,
    }));

    // ---------- helpers ----------
    const clamp = (v, a, b) => Math.min(b, Math.max(a, v));
    const softLine = (x1, y1, x2, y2, w, col, alpha) => {
      ctx.save();
      ctx.strokeStyle = col;
      ctx.lineWidth = w;
      ctx.globalAlpha = alpha;
      ctx.lineCap = "round";
      ctx.shadowColor = col;
      ctx.shadowBlur = w * 2.2;
      ctx.beginPath();
      ctx.moveTo(x1, y1);
      ctx.lineTo(x2, y2);
      ctx.stroke();
      ctx.restore();
    };

    // ---------- draw loop ----------
    let t = 0;
    const draw = () => {
      t += 0.016;

      // background: watercolor dusk + dreamy mist
      const grd = ctx.createLinearGradient(0, 0, 0, canvas.height);
      grd.addColorStop(0, "#f6e7d8"); // warm dawn
      grd.addColorStop(0.38, "#e7d5f4"); // lilac haze
      grd.addColorStop(0.75, "#cfe6ff"); // cool sky
      grd.addColorStop(1, "#f9f1e7"); // warm ground light
      ctx.fillStyle = grd;
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // vignetting (soft, hand-made)
      const vignette = ctx.createRadialGradient(
        canvas.width * 0.5,
        canvas.height * 0.42,
        Math.min(canvas.width, canvas.height) * 0.22,
        canvas.width * 0.5,
        canvas.height * 0.42,
        Math.max(canvas.width, canvas.height) * 0.9
      );
      vignette.addColorStop(0, "rgba(255,255,255,0)");
      vignette.addColorStop(0.65, "rgba(21, 24, 44, 0.075)");
      vignette.addColorStop(1, "rgba(12, 14, 28, 0.22)");
      ctx.fillStyle = vignette;
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // soft rain (very gentle)
      for (const p of rain) {
        p.y += p.sp;
        if (p.y > canvas.height + 30) {
          p.y = -40;
          p.x = Math.random() * canvas.width;
        }
        ctx.save();
        ctx.strokeStyle = "rgba(255,255,255,0.9)";
        ctx.globalAlpha = p.a;
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(p.x, p.y);
        ctx.lineTo(p.x + 8, p.y + p.len);
        ctx.stroke();
        ctx.restore();
      }

      // ink-haze particles (like tiny soot-dust / breath in light)
      for (const p of particles) {
        const wob = Math.sin(t * 0.9 + p.x * 0.002) * 0.18;
        p.x += p.vx + wob;
        p.y += p.vy + Math.sin(t * 0.6 + p.y * 0.001) * 0.08;

        if (p.x < -30) p.x = canvas.width + 30;
        if (p.x > canvas.width + 30) p.x = -30;
        if (p.y < -30) p.y = canvas.height + 30;
        if (p.y > canvas.height + 30) p.y = -30;

        ctx.save();
        ctx.fillStyle = "rgba(20, 24, 38, 1)";
        ctx.globalAlpha = p.a;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();
      }

      // floating lanterns (warm, hand-drawn outline + bloom)
      for (const L of lanterns) {
        const sway = Math.sin(t * L.drift + L.x * 0.001) * 10;
        const lift = Math.sin(t * 0.7 + L.y * 0.001) * 6;

        const x = L.x + sway;
        const y = L.y + lift;

        // glow halo
        ctx.save();
        ctx.fillStyle = `rgba(255, 235, 195, ${0.18 + L.glow * 0.22})`;
        ctx.beginPath();
        ctx.arc(x, y, 92 * L.s, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();

        // paper body (soft warm)
        ctx.save();
        ctx.fillStyle = `rgba(255, 244, 226, ${0.9})`;
        ctx.strokeStyle = "rgba(88, 64, 38, 0.55)";
        ctx.lineWidth = 2.2;
        ctx.lineJoin = "round";
        ctx.shadowColor = "rgba(255, 210, 166, 0.55)";
        ctx.shadowBlur = 14;

        const w = 54 * L.s;
        const h = 68 * L.s;

        ctx.beginPath();
        ctx.moveTo(x - w * 0.55, y - h * 0.42);
        ctx.quadraticCurveTo(x, y - h * 0.78, x + w * 0.55, y - h * 0.42);
        ctx.quadraticCurveTo(x + w * 0.9, y, x + w * 0.55, y + h * 0.42);
        ctx.quadraticCurveTo(x, y + h * 0.78, x - w * 0.55, y + h * 0.42);
        ctx.quadraticCurveTo(x - w * 0.9, y, x - w * 0.55, y - h * 0.42);
        ctx.closePath();
        ctx.fill();
        ctx.stroke();
        ctx.restore();

        // hanging string (soft line)
        softLine(x, y + h * 0.42, x, y + h * 0.42 + 38 * L.s, 1.4, "rgba(80, 58, 38, 0.65)", 0.9);
      }

      rafRef.current = requestAnimationFrame(draw);
    };

    draw();

    return () => {
      window.removeEventListener("resize", resize);
      cancelAnimationFrame(rafRef.current);
    };
  }, []);

  return (
    <div className="ghibli-asmr-root">
      <canvas ref={canvasRef} className="ghibli-asmr-canvas" />

      <motion.div
        className="ghibli-asmr-overlay"
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1.6, ease: "easeOut" }}
      >
        <motion.div
          className="ghibli-asmr-title"
          animate={{ opacity: [0.75, 1, 0.75] }}
          transition={{ duration: 5.2, repeat: Infinity, ease: "easeInOut" }}
        >
          soft light · slow breath
        </motion.div>
        <div className="ghibli-asmr-sub">
          (keep watching — everything settles on its own)
        </div>
      </motion.div>
    </div>
  );
}
